import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from '@material-ui/core';

import { ReactComponent as CheckMark } from '../../assets/svgs/checkmark.svg';
import { ReactComponent as IconClose } from '../../assets/svgs/icon-close.svg';
import { ReactComponent as IconDropDownArrow } from '../../assets/svgs/icon-dropdown-arrow.svg';
import { ReactComponent as IconTriangleWarning } from '../../assets/svgs/icon-triangle-warning.svg';
import { ClientFilter, MatchStatus } from './FilterSection';
import { ClientSummaryNode } from '../../generated/webapp_gql';
import { useOutsideClick } from '../../hooks';

export interface ActiveFilter {
  filterKey: ClientFilter;
  displayValue: string;
}

interface Props {
  isOpen: boolean;
  bulkMessageText: string;
  setBulkMessageText: (value: string) => void;
  closeDialog: () => void;
  onPreview: () => void;
  selectedMembers: ClientSummaryNode[];
  activeFilters: ActiveFilter[];
  setMembersToBulkMessage: (clients: ClientSummaryNode[]) => void;
}

interface DefaultMember extends ClientSummaryNode {
  selected: boolean;
  isEligible: boolean;
}

export enum BulkMessageModalTestID {
  BulkMessageModalCloseButton = 'BulkMessageModalCloseButton',
  BulkMessageModalCancelButton = 'BulkMessageModalCancelButton',
  BulkMessageModalPreviewButton = 'BulkMessageModalPreviewButton',
  BulkMessageModalTextInput = 'BulkMessageModalTextInput',
  BulkMessageModalDropdown = 'BulkMessageModalDropdown',
  BulkMessageModalDropdownSelectBox = 'BulkMessageModalDropdownSelectBox',
}

export const BulkMessageModal: React.FC<Props> = ({
  closeDialog,
  isOpen,
  onPreview,
  bulkMessageText,
  setBulkMessageText,
  selectedMembers,
  activeFilters,
  setMembersToBulkMessage,
}) => {
  const [showMatchesList, setShowMatchesList] = useState(false);
  const listRef = useRef(null);

  useOutsideClick(listRef, () => {
    setShowMatchesList(false);
  });

  const [defaultMembers, setDefaultMembers] = useState<DefaultMember[]>([]);

  useEffect(() => {
    const nextDefault = selectedMembers.map((member) => {
      return {
        ...member,
        isEligible: member.matchStatus !== MatchStatus.Unmatched,
        selected: true,
      };
    });

    setDefaultMembers(nextDefault);
  }, [selectedMembers]);

  const ineligibleMembers = selectedMembers.filter((member) => {
    return member.matchStatus === MatchStatus.Unmatched;
  });

  const eligibleMembers = defaultMembers.filter((member) => {
    return member.isEligible && member.selected;
  });

  const handleSelectCheckBox = (match: DefaultMember) => {
    match.selected = !match.selected;

    const nextMembers = defaultMembers.map((member) => {
      if (member.memberUuid === match.memberUuid) {
        return match;
      }

      return member;
    });

    setDefaultMembers(nextMembers);
  };

  return (
    <Dialog open={isOpen} onClose={closeDialog} aria-labelledby="form-dialog-title" fullWidth>
      <div className="h-full px-12 py-10 relative">
        <div
          onClick={closeDialog}
          data-testid={BulkMessageModalTestID.BulkMessageModalCloseButton}
          className="bg-gray-300 hover:bg-gray-200 cursor-pointer rounded-full h-10 w-10 absolute top-4 right-4 flex justify-center items-center"
        >
          <IconClose className="w-3 h-3 text-gray-1000 fill-current stroke-current" />
        </div>
        <h1 className="font-bold text-black text-2xl mb-2">Send Bulk Chat Messages</h1>
        <p className="text-gray-600 text-base mb-8">
          Allows you to send mass messages to your clients based on filters, which reduces manual
          work.
        </p>

        {activeFilters.length > 0 && (
          <>
            <p className="text-gray-1000 text-base mb-1 font-medium">Filters:</p>
            <div className="flex flex-wrap mb-6">
              {activeFilters.map(({ filterKey, displayValue }) => {
                return (
                  <div
                    className="rounded bg-blue-100 p-2 mr-2 mb-2 whitespace-pre"
                    key={`filter-${filterKey}`}
                  >
                    <p className="text-blue-200 font-semibold text-sm">{displayValue}</p>
                  </div>
                );
              })}
            </div>
          </>
        )}
        <p className="text-gray-1000 text-base mb-1 font-medium">Selected clients:</p>
        <div ref={listRef} className="w-full relative mb-8 h-10 cursor-pointer">
          <div
            data-testid={BulkMessageModalTestID.BulkMessageModalDropdown}
            onClick={() => setShowMatchesList(!showMatchesList)}
            className={`${
              showMatchesList ? 'shadow-lg rounded-b-0 rounded-t border-b-0' : ' rounded '
            } p-2 text-blue-200 font-medium border border-blue-200 bg-blue-100 flex flex-row justify-between items-center w-full`}
          >
            {defaultMembers.length === 1 ? (
              <p>
                <span className="font-bold">{`${defaultMembers.length} client available`}</span>
              </p>
            ) : (
              <p>
                <span className="font-bold">{`${defaultMembers.length} clients available`}</span>
              </p>
            )}
            <IconDropDownArrow className="text-blue-200 stroke-current fill-current h-2 w-2" />
          </div>
          {showMatchesList && (
            <div
              className={`${
                showMatchesList ? 'shadow-lg rounded-t-0 rounded-b' : 'rounded '
              } bg-white border border-blue-200 absolute w-full max-h-48 overflow-y-auto`}
            >
              {defaultMembers
                .sort((a, b) => {
                  return Number(b.isEligible) - Number(a.isEligible);
                })
                .map((match, i) => {
                  return (
                    <div
                      className={`${i === 0 ? '' : 'border-t border-gray-200'} ${
                        !match.isEligible ? 'bg-orange-200' : ''
                      } flex flex-row items-start p-2`}
                      key={match.memberUuid}
                    >
                      {match.isEligible && (
                        <div
                          onClick={() => handleSelectCheckBox(match)}
                          data-testid={BulkMessageModalTestID.BulkMessageModalDropdownSelectBox}
                          className={`${
                            match.selected
                              ? 'border-2 border-blue-200 bg-blue-200'
                              : 'border-2 border-gray-300 w-4 h-4'
                          } mr-3 rounded`}
                        >
                          {match.selected && <CheckMark className="w-3 h-3 text-white" />}
                        </div>
                      )}

                      {!match.isEligible && <div className="mr-3 w-4 h-4" />}
                      <p
                        className={`${
                          !match.isEligible ? 'text-orange-500' : 'text-gray-1000'
                        } font-bold  mr-4`}
                      >
                        {match.memberFirstName} {match.memberLastName}
                      </p>
                      <p
                        className={`${
                          !match.isEligible ? 'text-orange-500' : 'text-gray-1000'
                        } text-gray-600 mr-2`}
                      >
                        {match.companyName}
                      </p>
                      <p
                        className={`${
                          !match.isEligible ? 'text-orange-500' : 'text-gray-1000'
                        } text-gray-600 mr-2`}
                      >
                        {match.memberEmail}
                      </p>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
        {ineligibleMembers.length !== 0 && (
          <div className="bg-orange-200 rounded-full w-full p-2 mb-4 flex flex-row items-center">
            <IconTriangleWarning className="h-12 w-12 text-orange-500 fill-current stroke-current mx-3" />
            <p className="text-orange-500 font-medium">
              {ineligibleMembers.length === 1
                ? `${ineligibleMembers.length} client is no longer matched so you won’t be able to send them a bulk chat message.`
                : `${ineligibleMembers.length} clients are no longer matched so you won’t be able to send them bulk chat messages.`}
            </p>
          </div>
        )}
        <p className="text-gray-1000 text-base mb-1 font-medium">Message:</p>
        <textarea
          data-testid={BulkMessageModalTestID.BulkMessageModalTextInput}
          value={bulkMessageText}
          onChange={(e) => setBulkMessageText(e.target.value)}
          className="outline-none w-full rounded border border-gray-300 text-gray-700 placeholder-gray-600 p-2 mb-8 resize-none focus:border-blue-200"
          placeholder="Type message here"
          rows={5}
        />
        <div className="flex flex-row justify-end">
          <button
            data-testid={BulkMessageModalTestID.BulkMessageModalCancelButton}
            className="outline-none h-10 flex font-extrabold justify-center items-center text-blue-200 p-2 px-4 rounded border border-blue-200 mr-4"
            onClick={closeDialog}
          >
            Cancel
          </button>
          {bulkMessageText.length > 0 &&
          eligibleMembers.length > 0 &&
          ineligibleMembers.length !== defaultMembers.length ? (
            <button
              data-testid={BulkMessageModalTestID.BulkMessageModalPreviewButton}
              onClick={() => {
                setMembersToBulkMessage(eligibleMembers);
                onPreview();
              }}
              className="outline-none h-10 flex font-extrabold justify-center items-center text-white p-2 px-4 rounded hover:shadow-md bg-blue-200"
            >
              Preview Message
            </button>
          ) : (
            <div>
              <button
                data-testid={BulkMessageModalTestID.BulkMessageModalPreviewButton}
                className="outline-none cursor-not-allowed h-10 flex font-medium justify-center items-center text-gray-500 p-2 px-4 rounded border border-gray-300"
                disabled
              >
                Preview Message
              </button>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
